import {
  Component,
  ComponentRef,
  computed,
  effect,
  inject,
  OnDestroy,
  ViewContainerRef,
  viewChild,
} from '@angular/core';

import {ProjectStepStateService} from '../project-step-state.service';
import {TranslocoPipe} from '@jsverse/transloco';
import {ProjectChapterType, ProjectStepType} from '@frontend/shared';

@Component({
  selector: 'app-project-content-body',
  imports: [TranslocoPipe],
  templateUrl: './project-content-body.component.html',
})
export class ProjectContentBodyComponent implements OnDestroy {
  private readonly stepState = inject(ProjectStepStateService);

  readonly dynamicComponentContainer = viewChild('dynamicComponentContainer', {
    read: ViewContainerRef,
  });

  private currentComponentRef: ComponentRef<any> | null = null;
  private previousStep: {chapterId: ProjectChapterType; stepId: ProjectStepType} | null = null;

  protected readonly activeStep = computed(() => this.stepState.activeStep());
  protected readonly activeStepComponent = computed(() => this.getActiveStepComponent());

  constructor() {
    effect(() => {
      const currentStep = this.activeStep();
      if (!this.isSameStep(currentStep, this.previousStep)) {
        this.previousStep = currentStep ? {...currentStep} : null;
        this.loadStepComponent();
      }
    });
  }

  ngOnDestroy() {
    this.clearCurrentComponent();
  }

  private isSameStep(
    step1: {chapterId: ProjectChapterType; stepId: ProjectStepType} | null,
    step2: {chapterId: ProjectChapterType; stepId: ProjectStepType} | null,
  ): boolean {
    if (!step1 && !step2) return true;
    if (!step1 || !step2) return false;
    return step1.chapterId === step2.chapterId && step1.stepId === step2.stepId;
  }

  private loadStepComponent() {
    this.clearCurrentComponent();

    if (!this.activeStep()) return;

    queueMicrotask(() => {
      const dynamicComponentContainer = this.dynamicComponentContainer();
      if (dynamicComponentContainer) {
        const component = this.activeStepComponent();
        if (component) {
          this.currentComponentRef = dynamicComponentContainer.createComponent(
            component,
          ) as ComponentRef<any>;
        }
      }
    });
  }

  private clearCurrentComponent() {
    if (this.currentComponentRef) {
      this.currentComponentRef.destroy();
      this.currentComponentRef = null;
    }
  }

  private getActiveStepComponent() {
    const active = this.activeStep();
    if (!active) return null;

    const chapter = this.stepState
      .chaptersWithStatuses()
      .find((ch) => ch.configId === active.chapterId);
    if (!chapter) return null;

    const step = chapter.steps.find((s) => s.configId === active.stepId);
    return step?.content || null;
  }
}
